import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Output } from '@angular/core';
import { isIdentityMatch } from '@app/core/misc/identity-matching';
import { AllInclusiveService } from '@app/core/services/all-inclusive.service';
import { CreateCloudDbWizardService } from '@app/shared/components/cloud-db-export-wizard/create-clouddb-wizard.service';
import { DialogService } from 'primeng/dynamicdialog';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { BlaCompany } from 'src/app/core/entity/bla-company';
import { Client } from 'src/app/core/entity/client';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { CloudCompanyService } from 'src/app/core/services/cloud-company.service';
import { BaseCloudImportExportComponent } from './base-cloud-import-export.component';
import { TableColumn } from 'src/app/shared/components/table/interfaces/table-column';
import { translateColumnHeaders } from 'src/app/shared/components/table/misc/translate-column-header';

@Component({
  selector: 'app-export-companies-to-cloud',
  templateUrl: './export-companies-to-cloud.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: BaseCloudImportExportComponent, useExisting: ExportCompaniesToCloudComponent },
    DialogService,
    CreateCloudDbWizardService,
    getTranslationProvider('ExportCompaniesToCloudComponent'),
  ],
})
export class ExportCompaniesToCloudComponent extends BaseCloudImportExportComponent {
  @Output() updated: EventEmitter<void> = new EventEmitter<void>();

  columns$: Observable<TableColumn[]>;
  clients$: Observable<Client[]>;
  selectedClientToExport: Client = null;
  disableAddButtons = false;
  exporting = false;

  private columns = [
    { field: 'corporateIdentity', header: 'orgNumber' },
    { field: 'name', header: 'name' },
    { field: 'customerNumber', header: 'customerNumber' },
    { field: 'responsibleUser', header: 'responsibleUser' },
    { field: 'firmType', header: 'companyType' },
    { field: 'action', sortDisabled: true },
  ];

  private loadedDataSubject = new BehaviorSubject([]);

  override get hideAddButton(): boolean {
    return true;
  }

  // eslint-disable-next-line max-params
  constructor(
    protected override cloudCompanyService: CloudCompanyService,
    private changeDetectorRef: ChangeDetectorRef,
    private allInclusiveService: AllInclusiveService,
    private createCloudDbWizardService: CreateCloudDbWizardService,
  ) {
    super(cloudCompanyService);

    this.clients$ = this.loadedDataSubject.pipe(
      map((clients: Client[]) => this.appendResponsibleUserPropToClients(clients)),
    );

    this.columns$ = this.onTranslationChangeMapTo(translateColumnHeaders(this.columns as TableColumn[]));
  }

  loadList(): void {
    const clientFilterFn = (client: Client) => client.type.name !== 'PERSON' && !client.archived;

    this.getListData(clientFilterFn).subscribe({
      next: (response) => {
        this.loadedDataSubject.next(response);
      },
    });
  }

  onAddButton(client: Client) {
    this.selectedClientToExport = client;

    if (this.allInclusiveService.isLoggedInWithBLTAService) {
      this.showExportWizardForOnboarding(client);
      return;
    }

    this.showExportWizardForCreateCloudDB(client);
  }

  execute(): void {}

  // Implementation of abstract parent method
  protected filterList(clients: Client[], blaCompanies: BlaCompany[]): Client[] {
    return this.filterClients(clients).filter(
      (client) => !blaCompanies.some((blaCompany) => isIdentityMatch(blaCompany.orgNumber, client.corporateIdentity)),
    );
  }

  private filterClients(clients: Client[]): Client[] {
    // exclude clients that is private person and without corp identity and has public key already
    return clients.filter(
      (client: Client) => client.type.name !== 'PERSON' && client.corporateIdentity && !client.cloudApiKey,
    );
  }

  private showExportWizardForOnboarding(client: Client) {
    this.createCloudDbWizardService.runOnboardingWizard(client).subscribe();
  }

  private showExportWizardForCreateCloudDB(client: Client) {
    this.disableAddButtons = true;

    this.createCloudDbWizardService.runCreateCloudDbWizard(client).subscribe({
      next: () => {
        this.loadList();
        this.updated.emit();
      },
      error: () => {
        this.resetButtonState();
      },
      complete: () => {
        this.resetButtonState();
      },
    });
  }

  private resetButtonState() {
    this.disableAddButtons = false;
    this.selectedClientToExport = null;
    this.changeDetectorRef.markForCheck();
  }

  private appendResponsibleUserPropToClients(clients: Client[]): Client[] {
    return clients.map((client) => ({ ...client, responsibleUser: client.responsible?.name || '' }));
  }
}
